import { Injectable, inject } from '@angular/core';
import { MapMarkerHandler, MapMarker, MarkerDetailLabels, TransformationService } from '@luzula/luzula-lib';
import { AufnahmePunktDocument, punktLabel, isValidLatLng, bodenformLabelString, idOf } from '../../data-model';

/**
 * Handler für die Konvertierung von AufnahmePunktDocument zu MapMarker.
 * Wird vom MapModule verwendet, um Bohrpunkte auf der Karte darzustellen.
 *
 * Dieser Handler kapselt alle datenmodell-spezifischen Zugriffe, sodass
 * die MapComponent generisch und datenmodell-unabhängig bleibt.
 */
@Injectable({ providedIn: 'root' })
export class BohrpunktMapMarkerHandler implements MapMarkerHandler<AufnahmePunktDocument> {
	private ts = inject(TransformationService);

	/**
	 * Konvertiert ein AufnahmePunktDocument zu einem MapMarker.
	 * Alle Felder werden hier befüllt, damit die MapComponent nicht
	 * auf das Datenmodell zugreifen muss.
	 */
	toMapMarker(bp: AufnahmePunktDocument): MapMarker<AufnahmePunktDocument> {
		const latLng = this.ts.from(bp.header.koordinaten);
		const typeId = idOf(bp.type);

		// Typen 16 und 70: spezielle Label-Behandlung
		const isSpecialType = typeId === 16 || typeId === 70;

		return {
			id: bp._id,
			latLng,
			data: bp,
			// Bei Typ 16/70 kein Top-Label, sonst nur Nummer (kein Typ-Name)
			label: isSpecialType ? undefined : punktLabel(bp, false),
			type: bp.type,
			author: bp.header.aufnahme?.kartierer,
			date: bp.header.aufnahme?.datum,
			detailLabels: this.getDetailLabels(bp)
		};
	}

	/**
	 * Prüft ob ein Dokument gültige Koordinaten hat.
	 */
	hasValidCoordinates(bp: AufnahmePunktDocument): boolean {
		const latLng = this.ts.from(bp.header.koordinaten);
		return isValidLatLng(latLng);
	}

	/**
	 * Erzeugt die Detail-Labels für Zoom-abhängige Anzeige.
	 */
	private getDetailLabels(bp: AufnahmePunktDocument): MarkerDetailLabels {
		const labels: MarkerDetailLabels = {};
		const typeId = idOf(bp.type);

		// Typ 16: nur [FBF] als Bodenform-Label
		if (typeId === 16) {
			const bf = bodenformLabelString(bp);
			if (bf) labels.bodenform = `[${bf}]`;
			// Keine Humusform, keine Bemerkung
			return labels;
		}

		// Typ 70: nur [HF] als Humusform-Label
		if (typeId === 70) {
			const hf = bp.header.humusform?.aufnahmeFlaeche;
			const hfValue = hf ? (hf.neu || hf.alt || '') : '';
			if (hfValue) labels.humusform = `[${hfValue}]`;
			// Keine Bodenform, keine Bemerkung
			return labels;
		}

		// Standard-Behandlung für alle anderen Typen:

		// Bodenform
		const bf = bodenformLabelString(bp);
		if (bf) labels.bodenform = bf;

		// Humusform oder Vegetationsformengruppe (bei Moorpunkten)
		if (bp.type === '7_Moorpunkt') {
			if (bp.header.vegetationsformengruppe) {
				labels.humusform = bp.header.vegetationsformengruppe;
			}
		} else {
			const hf = bp.header.humusform?.aufnahmeFlaeche;
			const hfValue = hf ? (hf.neu || hf.alt || '') : '';
			if (hfValue) labels.humusform = hfValue;
		}

		// STFG: Hydromorphiegruppe + Nährkraft + Stammfeuchte (z.B. "NK2")
		const stfg = bp.extendedHeader?.stfg;
		if (stfg) {
			const sf1 = stfg.stammFeuchteStufe?.first || '';
			const nk = stfg.naehrkraftStufe || '';
			const sf = stfg.stammFeuchteStufe?.second || '';
			const stfgLabel = `${sf1}${nk}${sf}`;
			if (stfgLabel) labels.stfg = stfgLabel;
		}

		// Bemerkung (gekürzt)
		const bemerkung = bp.header.bemerkungen?.trim();
		if (bemerkung) {
			const maxLen = 25;
			labels.bemerkung = bemerkung.length > maxLen
				? bemerkung.substring(0, maxLen) + '…'
				: bemerkung;
		}

		return labels;
	}
}
